import { useFormMutation } from "@sinch/hooks/useFormMutation";
import { useMuiForm } from "@sinch/hooks/useMuiForm";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { useEffect } from "react";
import { MessageFormFields, messageSchema } from "./formSchema";
import { Contactlist, SendClientReminder } from "./queries.graphql";
import {
  ContactlistQuery,
  ContactlistQueryVariables,
  SendClientReminderMutation,
  SendClientReminderMutationVariables,
} from "./queries.types";

/**
 * Properties for the ClientReminderDialog component.
 */
interface ClientReminderDialogProps {
  shiftId: string | number;

  /**
   * Callback function to be called upon successful clientReminder.
   */
  onSuccess: () => void;
}

export interface UseClientReminderDialogReturn {
  form: ReturnType<typeof useMuiForm<MessageFormFields>>;
  handleSubmit: () => Promise<void>;
  sending: boolean;
}

/**
 * Custom hook to handle the clientReminder dialog form and submission.
 */
export const useClientReminderDialog = ({
  shiftId,
  onSuccess,
}: ClientReminderDialogProps): UseClientReminderDialogReturn => {
  const { data } = useQueryHandler<ContactlistQuery, ContactlistQueryVariables>(
    Contactlist,
    {
      shiftId: String(shiftId),
    },
    { skip: !shiftId }
  );

  const message = data?.clientShiftReminderMessage?.message;

  const form = useMuiForm<MessageFormFields>({
    formOptions: {
      mode: "onChange",
      reValidateMode: "onChange",
      context: {
        recipients: data?.shiftContactlists ?? [],
      },
    },
    schema: messageSchema({
      defaultMessage: message,
    }),
  });

  useEffect(() => {
    form.reset({
      textMessage: message,
    });
  }, [shiftId, message]);

  const { handleSubmit, loading } = useFormMutation<
    SendClientReminderMutation,
    SendClientReminderMutationVariables,
    MessageFormFields
  >({
    mutation: {
      mutate: SendClientReminder,
    },
    form,
    variableMapper: ({ textMessage }) => ({
      shiftId: String(shiftId),
      message: textMessage,
    }),
  });

  const handleSend = async () => {
    try {
      const response = await handleSubmit();
      if (response?.sendClientReminderMessage.result) {
        onSuccess();
      }
    } catch (e: any) {
      console.error(e.message);
      return;
    }
  };

  return {
    form,
    handleSubmit: handleSend,
    sending: loading,
  };
};
